import type { CSSProperties, MouseEventHandler, Ref } from "react";

import { css, cx, styled } from "simplydo/react/styled";
import { Button, type ButtonProps } from "simplydo/react/ui/button";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Label, type LabelProps } from "simplydo/react/ui/label";

const CircularButtonWithNumberWrapper = styled.span`
  display: inline-flex;
  position: relative;
  vertical-align: middle;

  /* &&& outweighs the Button look's own colour/hover rules. */
  &&& > button {
    width: 30px;
    min-width: 30px;
    height: 30px;
    margin: 0;
    padding: 0;
    font-size: 20px;
    overflow: hidden;
    transition:
      background-color 0.25s,
      color 0.25s;

    ${Icon} {
      opacity: 1;
    }

    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }

  &&& > button:hover:not(.is-disabled) {
    opacity: 1;
    background-color: ${({ theme }) => theme.colors.primary.solid};
    color: ${({ theme }) => theme.colors.primary.onSolid};
  }
`;

const numberLabelClass = css`
  position: absolute;
  top: -6px;
  right: -8px;
  z-index: 300;
  pointer-events: none;
`;

type CircularButtonWithNumberProps = Omit<
  ButtonProps,
  "onClick" | "children" | "circular" | "style"
> & {
  ref?: Ref<HTMLButtonElement>;
  icon?: IconData;
  image?: string;
  labelProps?: LabelProps;
  count?: number | string;
  square?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  style?: CSSProperties;
};

const CircularButtonWithNumber = ({
  icon,
  image,
  count,
  labelProps = {},
  square,
  style,
  className,
  ...buttonProps
}: CircularButtonWithNumberProps) => (
  <CircularButtonWithNumberWrapper style={style}>
    <Button
      variant="soft"
      color="grey"
      size="small"
      circular={!square}
      {...buttonProps}
      className={className}
    >
      {icon ? <Icon icon={icon} /> : null}
      {image ? <img src={image} alt="" /> : null}
    </Button>
    {count ? (
      <Label
        {...labelProps}
        circular={labelProps.circular ?? true}
        size={labelProps.size ?? "small"}
        color={labelProps.color ?? "red"}
        className={cx(numberLabelClass, labelProps.className)}
      >
        {count}
      </Label>
    ) : null}
  </CircularButtonWithNumberWrapper>
);

export default CircularButtonWithNumber;
